import React, { type FunctionComponent, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import {
  GridList,
  type GridColumns,
  type GridTableOptions,
  withContainerSize,
  LoadingDots,
  spacing,
  type GridState,
} from '@retailer-platform/shared-components'
import { type Moment } from 'moment'
import { type StoreMetric } from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import { trackSorting } from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.tracking'
import { useRetailerId } from 'utils/routing/params.hooks'
import { type FiltersDropdownOption } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.types'
import { useTableColumns } from './utils/useTableColumns'
import { FiltersEmptyState } from './components/FiltersEmptyState'

const noop = function () {} // eslint-disable-line @typescript-eslint/no-empty-function

const GridListContainer = styled.div({
  flex: 1,
  padding: `${spacing.X20} ${spacing.X20} 0`,
  overflow: 'hidden',
})

const ExpandableGridList = withContainerSize(GridList)
interface StoreMetricsTableProps {
  isLoading: boolean
  showEditButton?: boolean
  data: StoreMetric[]
  date: Moment
  isWeek: boolean
  selectedFilters: FiltersDropdownOption[]
  onEditFiltersClicked: () => void
}

export const StoreMetricsTable: FunctionComponent<StoreMetricsTableProps> = ({
  isLoading,
  showEditButton = false,
  data,
  date,
  isWeek,
  selectedFilters,
  onEditFiltersClicked,
}) => {
  const retailerId = useRetailerId()

  const columns: GridColumns<StoreMetric> = useTableColumns({
    showEditButton,
    data,
    date,
    isWeek,
  })

  const handleTableStateChanged = useCallback(
    (state: GridState<StoreMetric>) => {
      if (state.sortBy?.[0]) {
        trackSorting({
          columnId: state.sortBy[0].id,
          isSortedDesc: state.sortBy[0].desc,
          retailerId,
        })
      }
    },
    [retailerId]
  )

  const tableOptions: GridTableOptions<StoreMetric> = useMemo(
    () => ({
      disableSortRemove: true,
    }),
    []
  )

  return (
    <GridListContainer>
      {isLoading ? (
        <LoadingDots centered />
      ) : (
        <ExpandableGridList
          columns={columns}
          data={data}
          canLoadMore={false}
          loadingMore={false}
          onLoadMore={noop}
          isLoading={false}
          tableOptions={tableOptions}
          onStateChanged={handleTableStateChanged}
          emptyStateShowHeader
          emptyState={
            <FiltersEmptyState
              onEditFiltersClicked={onEditFiltersClicked}
              hasSelectedFilters={Boolean(selectedFilters.length)}
            />
          }
        />
      )}
    </GridListContainer>
  )
}
